/**
 * Componente compartido para captura de firmas digitales
 * Consolidado desde múltiples ubicaciones para evitar duplicación
 */
import { Signature } from 'lucide-react';
import { useRef, useState, useEffect, useCallback } from 'react';

interface SignatureCaptureProps {
  onSignatureSaved: (signatureId: string) => void;
  onSignaturePreview?: (dataURL: string) => void;
  onSignatureComplete?: () => void;
}

interface SignaturePoint {
  x?: number;
  y?: number;
  type: 'start' | 'move' | 'end';
}

class SignaturePad {
  canvas: HTMLCanvasElement;
  container: HTMLDivElement;
  ctx: CanvasRenderingContext2D;
  isDrawing: boolean;
  lastX: number;
  lastY: number;
  points: SignaturePoint[];
  ratio: number;

  constructor(canvas: HTMLCanvasElement, container: HTMLDivElement) {
    this.canvas = canvas;
    this.container = container;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('No se pudo obtener el contexto del canvas');
    }
    this.ctx = context;
    this.isDrawing = false;
    this.lastX = 0;
    this.lastY = 0;
    this.points = [];
    this.ratio = 1;

    this.resizeCanvas();
    this.setupCanvas();
    this.addEventListeners();
  }

  resizeCanvas(): void {
    const rect = this.container.getBoundingClientRect();
    this.ratio = Math.max(window.devicePixelRatio || 1, 1);

    // Establecer dimensiones reales del canvas
    this.canvas.width = rect.width * this.ratio;
    this.canvas.height = rect.height * this.ratio;

    // Establecer tamaño CSS
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;

    // Escalar el contexto
    this.ctx.scale(this.ratio, this.ratio);
  }

  setupCanvas(): void {
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 2.5;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
  }

  addEventListeners(): void {
    this.canvas.addEventListener('mousedown', this.handleStart.bind(this));
    this.canvas.addEventListener('mousemove', this.handleMove.bind(this));
    this.canvas.addEventListener('mouseup', this.handleEnd.bind(this));
    this.canvas.addEventListener('mouseleave', this.handleEnd.bind(this));

    this.canvas.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.handleEnd.bind(this));
    this.canvas.addEventListener('touchcancel', this.handleEnd.bind(this));
  }

  getCoordinates(e: MouseEvent | TouchEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    let clientX: number;
    let clientY: number;

    if (e.type.startsWith('touch')) {
      const touch = (e as TouchEvent).touches[0] || (e as TouchEvent).changedTouches[0];
      if (!touch) {
        return { x: 0, y: 0 };
      }
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = (e as MouseEvent).clientX;
      clientY = (e as MouseEvent).clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  handleStart(e: MouseEvent | TouchEvent): void {
    e.preventDefault();
    this.isDrawing = true;
    const { x, y } = this.getCoordinates(e);
    this.lastX = x;
    this.lastY = y;

    // Normalizar coordenadas (0-1) para que sean independientes del tamaño
    const rect = this.container.getBoundingClientRect();
    this.points.push({
      x: x / rect.width,
      y: y / rect.height,
      type: 'start'
    });
  }

  handleMove(e: MouseEvent | TouchEvent): void {
    if (!this.isDrawing) return;
    e.preventDefault();

    const { x, y } = this.getCoordinates(e);

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    // Normalizar coordenadas
    const rect = this.container.getBoundingClientRect();
    this.points.push({
      x: x / rect.width,
      y: y / rect.height,
      type: 'move'
    });

    this.lastX = x;
    this.lastY = y;
  }

  handleEnd(e: Event): void {
    if (!this.isDrawing) return;
    if (e.type.startsWith('touch')) {
      e.preventDefault();
    }
    this.isDrawing = false;
    this.points.push({ type: 'end' });
  }

  isEmpty(): boolean {
    return this.points.filter(p => p.type !== 'end').length === 0;
  }

  clear(): void {
    const rect = this.container.getBoundingClientRect();
    this.ctx.clearRect(0, 0, rect.width, rect.height);
    this.points = [];
  }

  toDataURL(): string {
    // Tamaño estándar fijo para todas las firmas (landscape)
    const STANDARD_WIDTH = 800;
    const STANDARD_HEIGHT = 400;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = STANDARD_WIDTH;
    tempCanvas.height = STANDARD_HEIGHT;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      throw new Error('No se pudo obtener el contexto del canvas temporal');
    }

    // Fondo blanco
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, STANDARD_WIDTH, STANDARD_HEIGHT);

    // Configurar estilo de línea
    tempCtx.strokeStyle = '#000';
    tempCtx.lineWidth = 3;
    tempCtx.lineCap = 'round';
    tempCtx.lineJoin = 'round';

    // Redibujar la firma usando coordenadas normalizadas
    tempCtx.beginPath();
    this.points.forEach((point) => {
      if (point.type === 'start' && point.x !== undefined && point.y !== undefined) {
        const x = point.x * STANDARD_WIDTH;
        const y = point.y * STANDARD_HEIGHT;
        tempCtx.moveTo(x, y);
      } else if (point.type === 'move' && point.x !== undefined && point.y !== undefined) {
        const x = point.x * STANDARD_WIDTH;
        const y = point.y * STANDARD_HEIGHT;
        tempCtx.lineTo(x, y);
        tempCtx.stroke();
        tempCtx.beginPath();
        tempCtx.moveTo(x, y);
      } else if (point.type === 'end') {
        tempCtx.beginPath();
      }
    });

    return tempCanvas.toDataURL('image/png');
  }

  fromData(data: SignaturePoint[]): void {
    this.clear();
    this.points = [...data];

    if (data.length === 0) return;

    const rect = this.container.getBoundingClientRect();

    // Redibujar usando coordenadas normalizadas
    this.ctx.beginPath();
    data.forEach((point) => {
      if (point.type === 'start' && point.x !== undefined && point.y !== undefined) {
        const x = point.x * rect.width;
        const y = point.y * rect.height;
        this.ctx.moveTo(x, y);
      } else if (point.type === 'move' && point.x !== undefined && point.y !== undefined) {
        const x = point.x * rect.width;
        const y = point.y * rect.height;
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
      } else if (point.type === 'end') {
        this.ctx.beginPath();
      }
    });
  }

  toData(): SignaturePoint[] {
    return this.points;
  }

  redraw(): void {
    const data = this.toData();
    this.resizeCanvas();
    this.setupCanvas();
    if (data.length > 0) {
      this.fromData(data);
    }
  }
}

export function SignatureCapture({ onSignatureSaved, onSignaturePreview, onSignatureComplete }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const savedDataRef = useRef<SignaturePoint[]>([]);
  const [isResizing, setIsResizing] = useState(false);

  // Inicializar SignaturePad
  useEffect(() => {
    if (!isFullscreen || !canvasRef.current || !containerRef.current) return;

    try {
      const pad = new SignaturePad(canvasRef.current, containerRef.current);
      signaturePadRef.current = pad;

      if (savedDataRef.current.length > 0) {
        pad.fromData(savedDataRef.current);
        setIsEmpty(false);
      }

      let resizeTimeout: NodeJS.Timeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        setIsResizing(true);
        resizeTimeout = setTimeout(() => {
          if (signaturePadRef.current) {
            signaturePadRef.current.redraw();
          }
          setTimeout(() => setIsResizing(false), 100);
        }, 150);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      // Forzar resize inicial
      setTimeout(handleResize, 100);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
        clearTimeout(resizeTimeout);
      };
    } catch (error) {
      console.error('Error inicializando SignaturePad:', error);
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen || !signaturePadRef.current) return;

    const checkEmpty = () => {
      setIsEmpty(signaturePadRef.current?.isEmpty() ?? true);
    };

    const interval = setInterval(checkEmpty, 300);
    return () => clearInterval(interval);
  }, [isFullscreen]);

  const handleSave = useCallback(() => {
    if (!signaturePadRef.current) return;

    if (signaturePadRef.current.isEmpty()) {
      alert('Por favor firma primero');
      return;
    }

    try {
      const dataURL = signaturePadRef.current.toDataURL();
      savedDataRef.current = signaturePadRef.current.toData();

      if (onSignaturePreview) {
        onSignaturePreview(dataURL);
      }

      const tempId = `temp-${Date.now()}`;
      onSignatureSaved(tempId);

      // ← NUEVA LÍNEA: Ejecutar callback adicional si existe
      if (onSignatureComplete) {
        onSignatureComplete();
      }


      setIsFullscreen(false);
    } catch (error) {
      console.error('Error guardando firma:', error);
      alert('Error al guardar la firma');
    }
  }, [onSignatureSaved, onSignaturePreview, onSignatureComplete]);

  const handleClear = useCallback(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      savedDataRef.current = [];
      setIsEmpty(true);
    }
  }, []);

  const openFullscreen = useCallback(() => {
    setIsFullscreen(true);
    setIsEmpty(savedDataRef.current.length === 0);
  }, []);

  const closeFullscreen = useCallback(() => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      savedDataRef.current = signaturePadRef.current.toData();
    }
    setIsFullscreen(false);
  }, []);

  return (
    <>
      <button
        onClick={openFullscreen}
        className="w-full px-4 py-3 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 cursor-pointer transition-colors flex items-center justify-center gap-2"
        type="button"
      >
        <span><Signature /></span>
        Firmar Documento
      </button>

      {isFullscreen && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex justify-between items-center flex-shrink-0 shadow-lg">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span><Signature /></span>
              Firma Digital
            </h2>
            <button
              onClick={closeFullscreen}
              className="text-white hover:bg-white/20 text-2xl leading-none w-10 h-10 flex items-center justify-center rounded-lg transition-colors"
              aria-label="Cerrar"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 p-3 flex flex-col bg-gray-50 overflow-hidden">
            <div
              ref={containerRef}
              className="flex-1 border-3 border-blue-400 rounded-xl overflow-hidden bg-white shadow-xl relative"
              style={{ minHeight: '200px' }}
            >
              <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full touch-none"
                style={{ touchAction: 'none', cursor: 'crosshair' }}
              />
              {isResizing && (
                <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    🔄 Ajustando...
                  </div>
                </div>
              )}
            </div>
            <p className="text-center text-sm text-gray-600 mt-3 font-medium">
              {isEmpty
                ? '👆 Firma con tu dedo o mouse en el área blanca'
                : '✓ Firma capturada - Puedes seguir firmando o guardar'}
            </p>
          </div>

          <div className="p-3 bg-white border-t-2 border-gray-200 flex gap-3 flex-shrink-0 shadow-lg">
            <button
              onClick={handleClear}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all active:scale-95 flex items-center justify-center gap-2"
              type="button"
            >
              <span>🗑️</span>
              Limpiar
            </button>
            <button
              onClick={handleSave}
              disabled={isEmpty}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center justify-center gap-2"
              type="button"
            >
              <span></span>
              Guardar Firma
            </button>
          </div>
        </div>
      )}
    </>
  );
}
